import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { List } from 'immutable'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { Chip, Box } from '@mui/material'
import MuiTextFieldWrapper from 'gui/components/common/MuiTextField'
import Autosuggest from 'react-autosuggest'
import AutosuggestHighlightMatch from 'autosuggest-highlight/match'
import AutosuggestHighlightParse from 'autosuggest-highlight/parse'
import { getTitlesData as getTitlesDataAction } from 'services/titles/redux/actions'
import './index.scss'

function escapeRegexCharacters (str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function formatContentTagLabel (content) {
  if (content.contentType === 'tag') {
    return `${content.contentType} : ${content.bundle}`
  }
  return content.contentType
}

function getDisplayValue (suggestion) {
  if (suggestion) {
    return `${suggestion.title}`
  }
  return null
}

function getMatchingTitles (data, value) {
  if (!data) {
    return []
  }

  const escapedValue = escapeRegexCharacters(value.trim())
  if (escapedValue === '') {
    return []
  }

  const regex = new RegExp(`^|\\b${escapedValue}`, 'i')
  return data.filter((title) => regex.test(getDisplayValue(title)))
}

function getContentTypeChipColor (contentType) {
  switch (contentType) {
    case 'video':
      return 'primary'
    case 'series':
      return 'secondary'
    case 'person':
      return 'error'
    case 'tag':
      return 'info'
    default:
      return 'default'
  }
}

function getPublishedStatusChipColor (published) {
  return published ? 'success' : 'warning'
}

function getLanguageChipColor (language) {
  // Map language codes to Material-UI colors that match the existing language chip gradients
  const languageColorMap = {
    en: 'primary', // Teal/blue gradient -> primary (blue)
    es: 'secondary', // Purple gradient -> secondary (purple)
    fr: 'info', // Blue gradient -> info (light blue)
    de: 'warning', // Yellow gradient -> warning (orange/yellow)
  }
  return languageColorMap[language] || 'default'
}

function renderInputComponent (selected, displaySelection = true) {
  return function curriedRender (inputProps) {
    return (
      <div className="autosuggest-input">
        <MuiTextFieldWrapper {...inputProps} />
        {displaySelection && selected.title ? (
          <Box sx={{ mt: 0.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            {selected.image && (
              <Box sx={{ width: 89, height: 50, overflow: 'hidden', borderRadius: 1 }}>
                <img src={selected.image} width="89" height="50" style={{ objectFit: 'cover' }} />
              </Box>
            )}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ fontWeight: 'bold', mb: 0.5 }}>{selected.title}</Box>
              <Box
                sx={{
                  fontFamily: 'Courier, Monaco, monospace',
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                  mb: 0.5,
                }}
              >
                {selected.contentId}
              </Box>
              <Chip
                label={formatContentTagLabel(selected)}
                color={getContentTypeChipColor(selected.contentType)}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        ) : null}
      </div>
    )
  }
}

function renderSuggestion (suggestion, { query }) {
  const suggestionText = `${suggestion.title}`
  const matches = AutosuggestHighlightMatch(suggestionText, query)
  const parts = AutosuggestHighlightParse(suggestionText, matches)
  const published = suggestion.published ? 'published' : 'unpublished'

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', p: 1 }}>
      <Box
        sx={{ width: 89, height: 50, overflow: 'hidden', borderRadius: 1, mr: 1, flexShrink: 0 }}
      >
        <img src={suggestion.image} width="89" height="50" style={{ objectFit: 'cover' }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {parts.map((part, index) => {
            const className = part.highlight ? 'highlight' : null
            return (
              <span className={className} key={index}>
                {part.text}
              </span>
            )
          })}
        </Box>
        <Box
          sx={{
            fontFamily: 'Courier, Monaco, monospace',
            fontSize: '0.875rem',
            color: 'text.secondary',
            mb: 0.5,
          }}
        >
          {suggestion.contentId}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip
            label={formatContentTagLabel(suggestion)}
            color={getContentTypeChipColor(suggestion.contentType)}
            size="small"
            variant="outlined"
          />
          <Chip
            label={published}
            color={getPublishedStatusChipColor(suggestion.published)}
            size="small"
            variant="outlined"
          />
          {suggestion.language && (
            <Chip
              label={suggestion.language}
              color={getLanguageChipColor(suggestion.language)}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

function onSuggestKeypress (event) {
  if (event.key === 'Enter') {
    event.preventDefault()
  }
}

class TitleAutosuggest extends Component {
  constructor (props) {
    super(props)
    this.state = {
      value: props.title || '',
      suggestions: [],
      selectedSuggestion: {},
      isLoading: false,
    }
  }

  static getDerivedStateFromProps = (props, state) => {
    const { value, selectedSuggestion } = state
    const { titles, selectedValue } = props
    const titlesData = titles.get('data', List()).toJS()
    const derivedState = {
      isLoading: titles.get('processing', false),
      suggestions: getMatchingTitles(titlesData, value),
    }

    if (selectedValue) {
      if (
        selectedSuggestion &&
        selectedSuggestion.contentId &&
        selectedSuggestion.contentId === selectedValue.get('contentId')
      ) {
        derivedState.selectedSuggestion = selectedSuggestion
      } else {
        derivedState.selectedSuggestion = {
          contentId: selectedValue.get('contentId') || selectedValue.get('d6Id'),
          contentType: selectedValue.get('contentType') || selectedValue.get('type'),
          title: selectedValue.get('contentTitle') || selectedValue.get('value'),
        }
      }
    }
    return derivedState
  }

  onChange = (event, { newValue }) => {
    this.setState({
      value: newValue,
    })
  }

  onSuggestionSelected = (event, { suggestion }) => {
    const { onSelect } = this.props
    this.setState({
      selectedSuggestion: suggestion,
    })
    onSelect(suggestion)
  }

  onSuggestionsFetchRequested = ({ value }) => {
    const { getTitlesData, type, bundle } = this.props
    getTitlesData({ value, type, bundle })
  }

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
      value: '',
    })
  }

  render () {
    const { label, displaySelection = true, disabled = false } = this.props
    const { value, suggestions, selectedSuggestion } = this.state
    const inputProps = {
      disabled,
      label,
      value,
      onChange: this.onChange,
      onKeyDown: onSuggestKeypress,
    }

    return (
      <Box sx={{ width: '100%' }}>
        <Autosuggest
          suggestions={suggestions}
          inputProps={inputProps}
          getSuggestionValue={getDisplayValue}
          renderSuggestion={renderSuggestion}
          renderInputComponent={renderInputComponent(selectedSuggestion, displaySelection)}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          onSuggestionSelected={this.onSuggestionSelected}
          focusInputOnSuggestionClick={false} // For custom input
        />
      </Box>
    )
  }
}

TitleAutosuggest.propTypes = {
  label: PropTypes.string.isRequired,
  titles: ImmutablePropTypes.map,
  selectedValue: ImmutablePropTypes.map,
  onSelect: PropTypes.func.isRequired,
  getTitlesData: PropTypes.func.isRequired,
  displaySelection: PropTypes.bool, // used for tag display after selecting an option
  type: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  bundle: PropTypes.string,
}

export default connect(
  (state) => ({
    titles: state.titles,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        getTitlesData: getTitlesDataAction,
      },
      dispatch,
    ),
)(TitleAutosuggest)
